import React, { useState, useEffect, useRef } from 'react';
import { User, MapPin, Target, Award, Instagram, Shield, Mail, Lock, ArrowLeft, CheckCircle, Camera, Upload, X, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { playerAPI } from '../../services/community';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const CreatePlayerProfile = ({ onBack, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const fileInputRef = useRef(null);
  const [options, setOptions] = useState({
    experience_levels: [],
    positions: [],
    play_styles: [],
    bio_example: '',
  });

  const [formData, setFormData] = useState({
    name: '',
    nickname: '',
    location: '',
    experience_level: 'Beginner',
    favorite_position: '',
    primary_marker: '',
    years_playing: '',
    play_style: '',
    home_field: '',
    championships: '',
    awards: '',
    instagram_handle: '',
    bio: '',
    past_teams: '',
    recovery_email: '',
    profile_password: '',
    confirm_password: '',
    profile_picture: '',
  });

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const data = await playerAPI.getOptions();
        setOptions(data);
      } catch (err) {
        console.error('Error fetching options:', err);
      }
    };
    fetchOptions();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'profile');

      const response = await fetch(`${API_BASE_URL}/api/uploads/image?type=profile`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setFormData(prev => ({ ...prev, profile_picture: `${API_BASE_URL}${data.url}` }));
    } catch (err) {
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = () => {
    setFormData(prev => ({ ...prev, profile_picture: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name || !formData.recovery_email || !formData.profile_password) {
      setError('Name, recovery email, and password are required');
      return;
    }

    if (formData.profile_password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    if (formData.profile_password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { confirm_password, ...submitData } = formData;
      submitData.years_playing = submitData.years_playing ? parseInt(submitData.years_playing) : null;
      
      const result = await playerAPI.create(submitData);
      setSuccess(true);
      
      // Store player info in localStorage
      localStorage.setItem('gwins_player', JSON.stringify(result.player));
      
      if (onSuccess) {
        setTimeout(() => onSuccess(result.player), 2000);
      }
    } catch (err) {
      setError(err.message || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0b0c] flex items-center justify-center p-4">
        <div className="bg-[#131416] rounded-2xl p-8 max-w-md w-full text-center border border-[#00d4aa]/30">
          <CheckCircle className="h-16 w-16 text-[#00d4aa] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Profile Created!</h2>
          <p className="text-white/60 mb-4">Welcome to the GWINS community, {formData.name}!</p>
          <p className="text-sm text-white/40">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0b0c] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-white/60 hover:text-white mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-black text-white uppercase">Create Player Profile</h1>
          <p className="text-white/60 mt-2">Join the GWINS community and connect with players</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
              {error}
            </div>
          )}

          {/* Profile Photo */}
          <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Camera className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Profile Photo
            </h2>
            
            <div className="flex items-center gap-6">
              <div className="relative">
                <div className="w-28 h-28 rounded-full bg-[#1a1c1f] border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden">
                  {formData.profile_picture ? (
                    <img src={formData.profile_picture} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User className="h-10 w-10 text-white/30" />
                  )}
                </div>
                {formData.profile_picture && (
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4 text-white" />
                  </button>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="profile-photo-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="border-[#00d4aa]/50 text-[#00d4aa] hover:bg-[#00d4aa]/10 w-full"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-[#00d4aa] border-t-transparent rounded-full animate-spin mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Photo
                    </>
                  )}
                </Button>
                <p className="text-white/40 text-xs">JPG, PNG, GIF or WebP. Max 5MB.</p>
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <User className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Basic Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Name *</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Nickname / Callsign</label>
                <Input
                  name="nickname"
                  value={formData.nickname}
                  onChange={handleChange}
                  placeholder="e.g., Thunder, Sniper"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-white/70 text-sm mb-2">Location</label>
                <Input
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="City, State/Country"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* Experience & Play Style */}
          <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Target className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Experience & Play Style
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Experience Level</label>
                <select
                  name="experience_level"
                  value={formData.experience_level}
                  onChange={handleChange}
                  className="w-full bg-[#1a1c1f] border border-white/10 text-white rounded-md px-3 py-2"
                >
                  {options.experience_levels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Favorite Position</label>
                <select
                  name="favorite_position"
                  value={formData.favorite_position}
                  onChange={handleChange}
                  className="w-full bg-[#1a1c1f] border border-white/10 text-white rounded-md px-3 py-2"
                >
                  <option value="">Select Position</option>
                  {options.positions.map(pos => (
                    <option key={typeof pos === 'object' ? pos.value : pos} value={typeof pos === 'object' ? pos.value : pos}>
                      {typeof pos === 'object' ? pos.value : pos}
                    </option>
                  ))}
                </select>
                {formData.favorite_position && options.positions.find(p => p.value === formData.favorite_position)?.description && (
                  <p className="text-[#00d4aa]/70 text-xs mt-1">
                    {options.positions.find(p => p.value === formData.favorite_position)?.description}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Play Style</label>
                <select
                  name="play_style"
                  value={formData.play_style}
                  onChange={handleChange}
                  className="w-full bg-[#1a1c1f] border border-white/10 text-white rounded-md px-3 py-2"
                >
                  <option value="">Select Style</option>
                  {options.play_styles.map(style => (
                    <option key={typeof style === 'object' ? style.value : style} value={typeof style === 'object' ? style.value : style}>
                      {typeof style === 'object' ? style.value : style}
                    </option>
                  ))}
                </select>
                {formData.play_style && options.play_styles.find(s => s.value === formData.play_style)?.description && (
                  <p className="text-[#00d4aa]/70 text-xs mt-1">
                    {options.play_styles.find(s => s.value === formData.play_style)?.description}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Years Playing</label>
                <Input
                  name="years_playing"
                  type="number"
                  value={formData.years_playing}
                  onChange={handleChange}
                  placeholder="e.g., 5"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                  min="0"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-white/70 text-sm mb-2">Primary Marker</label>
                <Input
                  name="primary_marker"
                  value={formData.primary_marker}
                  onChange={handleChange}
                  placeholder="e.g., Planet Eclipse CS3, Dye M3+"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-white/70 text-sm mb-2">Home Field</label>
                <Input
                  name="home_field"
                  value={formData.home_field}
                  onChange={handleChange}
                  placeholder="e.g., Hollywood Sports Park, SC Village"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* Achievements */}
          <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Award className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Achievements & History
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Championships & Titles</label>
                <Input
                  name="championships"
                  value={formData.championships}
                  onChange={handleChange}
                  placeholder="e.g., NXL World Cup 2023 Champion, PSP Series Winner"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Awards & Achievements</label>
                <Input
                  name="awards"
                  value={formData.awards}
                  onChange={handleChange}
                  placeholder="e.g., MVP NXL Chicago 2023, All-Star Team Selection"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Past Teams</label>
                <Input
                  name="past_teams"
                  value={formData.past_teams}
                  onChange={handleChange}
                  placeholder="e.g., Dynasty 2018-2020, Impact 2020-2022"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* Social & Bio */}
          <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Instagram className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Social & Bio
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Instagram Handle</label>
                <Input
                  name="instagram_handle"
                  value={formData.instagram_handle}
                  onChange={handleChange}
                  placeholder="@yourhandle"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Bio</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  placeholder="Tell us about yourself and your paintball journey..."
                  rows={4}
                  className="w-full bg-[#1a1c1f] border border-white/10 text-white rounded-md px-3 py-2 resize-none"
                />
                <div className="mt-2 p-3 bg-white/5 rounded-lg border border-white/10">
                  <p className="text-white/50 text-xs font-medium mb-1">Example Bio:</p>
                  <p className="text-white/40 text-xs italic">
                    {options.bio_example || "I've been playing paintball for 5 years, starting in local rec games before joining competitive speedball. I love the adrenaline rush of snake plays and have competed in regional NXL events. Looking to connect with other players and find a team in the Miami area. Off the field, I enjoy working on my markers and watching pro paintball streams."}
                  </p>
                </div>
                <p className="text-white/40 text-xs mt-2">Keep content respectful and appropriate</p>
              </div>
            </div>
          </div>

          {/* Account Security */}
          <div className="bg-[#131416] rounded-xl p-6 border border-[#00d4aa]/30">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Shield className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Account Security
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Recovery Email *</label>
                <Input
                  name="recovery_email"
                  type="email"
                  value={formData.recovery_email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                  required
                />
                <p className="text-white/40 text-xs mt-1">Used for password recovery</p>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Profile Password *</label>
                <div className="relative">
                  <Input
                    name="profile_password"
                    type={showPassword ? "text" : "password"}
                    value={formData.profile_password}
                    onChange={handleChange}
                    placeholder="To edit profile later"
                    className="bg-[#1a1c1f] border-white/10 text-white pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Confirm Password *</label>
                <div className="relative">
                  <Input
                    name="confirm_password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirm_password}
                    onChange={handleChange}
                    placeholder="Confirm password"
                    className="bg-[#1a1c1f] border-white/10 text-white pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="flex-1 border-white/20 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
            >
              {loading ? 'Creating...' : 'CREATE PROFILE'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePlayerProfile;
